/**
 * EUR Currency Formatting Utilities
 * Spanish locale currency formatting
 */

const SPANISH_LOCALE = 'es-ES'
const CURRENCY = 'EUR'

/**
 * Format number as EUR currency
 * e.g., 1234.56 -> "1.234,56 €"
 */
export function formatCurrency(
  amount: number,
  options: {
    showSymbol?: boolean
    decimals?: number
  } = {}
): string {
  const { showSymbol = true, decimals = 2 } = options

  const formatted = new Intl.NumberFormat(SPANISH_LOCALE, {
    style: showSymbol ? 'currency' : 'decimal',
    currency: CURRENCY,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount)

  return formatted
}

/**
 * Format as compact currency (for large amounts)
 * e.g., 1500 -> "1,5K €"
 */
export function formatCompactCurrency(amount: number): string {
  if (amount < 1000) {
    return formatCurrency(amount)
  }

  const formatter = new Intl.NumberFormat(SPANISH_LOCALE, {
    style: 'currency',
    currency: CURRENCY,
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 1,
  })

  return formatter.format(amount)
}

/**
 * Format as short currency without decimals if whole number
 * e.g., 5.00 -> "5 €", 5.50 -> "5,50 €"
 */
export function formatCurrencyShort(amount: number): string {
  const isWhole = amount % 1 === 0
  return formatCurrency(amount, { decimals: isWhole ? 0 : 2 })
}

/**
 * Format as positive/negative currency with color class
 */
export function formatCurrencyWithSign(amount: number): {
  text: string
  class: string
} {
  const formatted = formatCurrency(Math.abs(amount))

  if (amount > 0) {
    return { text: `+${formatted}`, class: 'text-green-600' }
  } else if (amount < 0) {
    return { text: `-${formatted}`, class: 'text-red-600' }
  }
  return { text: formatted, class: 'text-gray-600' }
}

/**
 * Format reward amount for display
 * e.g., "5,00 € por venta"
 */
export function formatRewardAmount(amount: number, suffix?: string): string {
  const formatted = formatCurrency(amount)
  return suffix ? `${formatted} ${suffix}` : formatted
}

/**
 * Format balance display
 */
export function formatBalance(
  available: number,
  pending?: number
): {
  available: string
  pending?: string
  total?: string
} {
  const result: {
    available: string
    pending?: string
    total?: string
  } = {
    available: formatCurrency(available),
  }

  if (pending !== undefined && pending > 0) {
    result.pending = formatCurrency(pending)
    result.total = formatCurrency(available + pending)
  }

  return result
}

/**
 * Parse currency string to number
 * e.g., "1.234,56 €" -> 1234.56
 */
export function parseCurrency(value: string): number | null {
  if (!value) return null

  // Remove currency symbol and whitespace
  let cleaned = value.replace(/[€\s]/g, '')

  // Handle Spanish format (1.234,56) by converting to standard (1234.56)
  // Remove thousand separators (periods) and replace decimal comma with period
  cleaned = cleaned.replace(/\./g, '').replace(',', '.')

  const num = parseFloat(cleaned)
  return isNaN(num) ? null : num
}

/**
 * Calculate percentage of total
 */
export function calculatePercentage(amount: number, total: number): number {
  if (total === 0) return 0
  return Math.round((amount / total) * 100)
}

/**
 * Format percentage
 */
export function formatPercentage(value: number): string {
  return new Intl.NumberFormat(SPANISH_LOCALE, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value / 100)
}

/**
 * Check if amount meets withdrawal threshold
 */
export function meetsWithdrawalThreshold(
  amount: number,
  threshold: number = 10
): boolean {
  return amount >= threshold
}

/**
 * Format progress towards threshold
 */
export function formatProgressToThreshold(
  current: number,
  threshold: number = 10
): {
  current: string
  threshold: string
  remaining: string
  percentage: number
  canWithdraw: boolean
} {
  const remaining = Math.max(0, threshold - current)
  const percentage = Math.min(100, calculatePercentage(current, threshold))

  return {
    current: formatCurrency(current),
    threshold: formatCurrency(threshold),
    remaining: formatCurrency(remaining),
    percentage,
    canWithdraw: current >= threshold,
  }
}

/**
 * Format number with thousand separators (Spanish style)
 */
export function formatNumber(num: number, decimals: number = 0): string {
  return new Intl.NumberFormat(SPANISH_LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num)
}
